<script lang="ts" setup>
import type { Component, ComputedRef } from 'vue'
import { computed, ref, watch } from 'vue'
import type { Activity, ActivityType, StudentProgress } from '~/models/Activity'
import { useActivityStore } from '~/stores/activity'
import { KsBadge, KsButton, KsDialog } from '@aschehoug/kloss'
import ActivityProgressBody from './ActivityProgressBody.vue'
import ActivityBasicBody from './ActivityBasicBody.vue'
import ActivityAssignmentBody from './ActivityAssignmentBody.vue'
import { TeleportationTarget } from '~/models/TeleportationTarget'
import { useI18n } from 'vue-i18n'

const { loadCompoundProgressByLocationsAsList } = useActivityStore()

const props = defineProps<{
  locationActivity: Activity
  cardTitle: string
  url: string
}>()

const { t } = useI18n()

const studentList = ref<StudentProgress[]>([])
const isLoading = ref<boolean>(true)
const dialogOpen = ref<boolean>(false)

watch(dialogOpen, async (isOpen) => {
  if (isOpen) {
    const locationId = typeof(props.locationActivity.locationId) === 'string'
      ? parseInt(props.locationActivity.locationId)
      : props.locationActivity.locationId
    isLoading.value = true
    studentList.value = await loadCompoundProgressByLocationsAsList([locationId])
    studentList.value.forEach((studentProgress) => {
      studentProgress.url = props.url
    })
    isLoading.value = false
  }
})

const cardActivity: ComputedRef<string> = computed(() => {
  if (!props.locationActivity) {
    return ''
  }

  return props.locationActivity.visitedUsers + '/' + props.locationActivity.totalUsers
})

const activityType = computed((): ActivityType => {
  // Show assignment/formWriter
  if (studentList.value.some((progress) => progress.formWriterId !== undefined && progress.formWriterId.length > 0))
  {
    return 'assignment'
  }

  // Show progress
  if (studentList.value.some((progress) => progress.sectionCount !== undefined && progress.sectionCount > 0)) {
    return 'progress'
  }

  // Show visits
  if(studentList.value.every((progress) => progress.sectionCount !== undefined && progress.sectionCount === 0)) {
    return 'basic'
  }

  return 'basic'
})

const getComponentType = (type: string): Component => {
  if(type === 'assignment') {
    return ActivityAssignmentBody
  }
  if(type === 'progress') {
    return ActivityProgressBody
  }
  if(type === 'basic') {
    return ActivityBasicBody
  }

  return ActivityBasicBody
}
</script>

<template>
  <KsButton
    icon-left="user"
    class="ml-2"
    variant="secondary"
    size="small"
    shape="square"
    :aria-label="t('activity.button.openActivity', { title: cardTitle })"
    @click="dialogOpen = true"
  >
    <KsBadge class="!-right-2 !-top-5">
      <span v-text="cardActivity" />
    </KsBadge>
  </KsButton>
  <Teleport :to="TeleportationTarget.AppTop">
    <KsDialog
      :title="cardTitle"
      :open="dialogOpen"
      size="medium"
      @close="dialogOpen = false"
    >
      <template #body>
        <component
          :is="getComponentType(activityType)"
          v-if="!isLoading"
          :student-list="studentList"
        />
      </template>
    </KsDialog>
  </Teleport>
</template>
